import { Form, Formik } from "formik";
import { FC } from "react";
import { object, string } from "zod";
import { toFormikValidationSchema } from "zod-formik-adapter";

import { removeInstLink } from "@/features/profile/utils";
import { Button, Input, Typography } from "@/shared/ui";
import { useOnboardingStore } from "@/stores";
import InstagramIcon from "@/svgs/instagram.svg";

import styles from "./instagram.module.scss";
import { InstagramTabFormScheme } from "./instagram.types";

const validationSchema = () => {
  return object({
    instagram: string()
      .min(1, "Field is required")
      .refine(
        (val) => {
          const { error } = parseInstagramInput(val);
          return !error;
        },
        {
          message: "The input is wrong. Please provide instagram profile link or username.",
        }
      ),
  });
};

export const parseInstagramInput = (input: string): { username?: string; error?: string } => {
  const trimmed = input.trim();

  if (/^@?[a-zA-Z0-9._]+$/.test(trimmed)) {
    return { username: trimmed.startsWith("@") ? trimmed : `@${trimmed}` };
  }

  let urlInput = trimmed;
  if (/^instagram\.com/.test(trimmed)) {
    urlInput = `https://${trimmed}`;
  } else if (/^www\.instagram\.com/.test(trimmed)) {
    urlInput = `https://${trimmed}`;
  }

  try {
    const url = new URL(urlInput);
    if (url.hostname.includes("instagram.com")) {
      const paths = url.pathname.split("/").filter(Boolean);
      if (paths.length === 1 && /^[a-zA-Z0-9._]+$/.test(paths[0] as string)) {
        return { username: `@${paths[0]}` };
      }
    }
  } catch {
    return { error: "The input is wrong. Please provide instagram profile link or username." };
  }

  return { error: "The input is wrong. Please provide instagram profile link or username." };
};

const InstagramTab: FC = () => {
  const currentStep = useOnboardingStore((state) => state.currentStep);
  const { setCurrentStep, setInfo, addPassedStep } = useOnboardingStore((state) => state.actions);

  const instagram = useOnboardingStore((state) => state.userInfo.socialLinks?.instagram);

  const onSubmitHandler = ({ instagram }: InstagramTabFormScheme) => {
    if (instagram) {
      setInfo({ socialLinks: { instagram: `https://www.instagram.com/${instagram.slice(1)}` } });
    }
    setCurrentStep(currentStep + 1);
    addPassedStep(currentStep);
  };

  return (
    <main className={styles.instagramTab}>
      <Typography semi variant="title-2">
        Enter your Instagram username
      </Typography>

      <Formik<InstagramTabFormScheme>
        validateOnMount
        validateOnBlur={false}
        validateOnChange={false}
        initialValues={{ instagram: removeInstLink(instagram ?? "") }}
        validationSchema={toFormikValidationSchema(validationSchema())}
        onSubmit={onSubmitHandler}
      >
        {({ values, touched, errors, dirty, isValid, setFieldValue, setFieldTouched, validateField }) => (
          <Form className={styles.form}>
            <Input
              placeholder="@username"
              value={values.instagram}
              startContent={<InstagramIcon className={styles.icon} />}
              variant={touched.instagram && errors.instagram ? "error" : undefined}
              message={touched.instagram ? errors.instagram : undefined}
              onBlur={() => setFieldTouched("instagram", true)}
              onValueChange={async (value) => {
                const { username, error } = parseInstagramInput(value);

                if (error) {
                  await setFieldValue("instagram", value);
                  await setFieldTouched("instagram", true);
                  return;
                }

                await setFieldValue("instagram", username);
                await validateField("instagram");
              }}
            />
            <Button
              fullWidth
              size="big"
              type="submit"
              disabled={
                (!dirty && !values.instagram) ||
                !isValid ||
                (values.instagram.length === 1 && values.instagram.includes("@"))
              }
            >
              Continue
            </Button>
          </Form>
        )}
      </Formik>
    </main>
  );
};

export { InstagramTab };
